/**
 * takeoffGeom.js — pure geometry + formatting helpers for the takeoff editor.
 *
 * Coordinate convention (load-bearing — see database/101_takeoffs.sql and
 * lib/pdfFrame.js): ALL stored geometry is page-relative VISUAL fractions
 * [fx, fy] (0..1, top-left origin, y down, rotation-aware). Physical math
 * converts fractions to PDF points on the VISUAL page via visualSize(frame)
 * (fractions are anisotropic — fx scales by vw, fy by vh — so distances and
 * areas must be computed in points, never in raw fraction space), then to real
 * feet through scale_points_per_foot (PDF points per foot).
 */

import { visualSize } from '../../lib/pdfFrame.js'
import { TAKEOFF_CATEGORIES } from '../../services/takeoffs.js'

/* ── Category colors ───────────────────────────────────────────────────────── */
// Distinct hues, one per category. Known categories map by their (stable) index
// in TAKEOFF_CATEGORIES so no two collide; unknown keys fall back to a hash.
export const CATEGORY_PALETTE = [
  '#dc2626', '#ea580c', '#d97706', '#ca8a04', '#65a30d', '#16a34a',
  '#0d9488', '#0891b2', '#2563eb', '#4f46e5', '#7c3aed', '#c026d3',
  '#db2777', '#e11d48', '#0ea5e9', '#84cc16', '#9333ea', '#78716c',
]

export function categoryColor(key) {
  const idx = TAKEOFF_CATEGORIES.findIndex(c => c.key === key)
  if (idx >= 0) return CATEGORY_PALETTE[idx % CATEGORY_PALETTE.length]
  let h = 0
  for (let i = 0; i < String(key).length; i++) h = (h * 31 + String(key).charCodeAt(i)) >>> 0
  return CATEGORY_PALETTE[h % CATEGORY_PALETTE.length]
}

/** '#rrggbb' + alpha → 'rgba(...)' for translucent fills. */
export function withAlpha(hex, a) {
  const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r},${g},${b},${a})`
}

/* ── Polygon / polyline math (unit-agnostic [x, y] pairs) ─────────────────── */

/** Shoelace formula — absolute area of a closed polygon. */
export function shoelaceArea(pts) {
  const n = pts.length
  let a = 0
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n
    a += pts[i][0] * pts[j][1] - pts[j][0] * pts[i][1]
  }
  return Math.abs(a) / 2
}

/** Total length of an OPEN path (no closing segment). */
export function openPathLength(pts) {
  let p = 0
  for (let i = 0; i < pts.length - 1; i++) p += Math.hypot(pts[i + 1][0] - pts[i][0], pts[i + 1][1] - pts[i][1])
  return p
}

/** Area-weighted centroid of a polygon (falls back to vertex average when degenerate). */
export function polygonCentroid(pts) {
  const n = pts.length
  let a = 0, cx = 0, cy = 0
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n
    const cross = pts[i][0] * pts[j][1] - pts[j][0] * pts[i][1]
    a += cross
    cx += (pts[i][0] + pts[j][0]) * cross
    cy += (pts[i][1] + pts[j][1]) * cross
  }
  if (Math.abs(a) < 1e-12) {
    let sx = 0, sy = 0
    for (const p of pts) { sx += p[0]; sy += p[1] }
    return [sx / n, sy / n]
  }
  return [cx / (3 * a), cy / (3 * a)]
}

/** Point halfway along an open path (for line labels). */
export function midpointAlong(pts) {
  const total = openPathLength(pts)
  if (total <= 0) return pts[0] ? [...pts[0]] : [0, 0]
  let walked = 0
  const half = total / 2
  for (let i = 0; i < pts.length - 1; i++) {
    const seg = Math.hypot(pts[i + 1][0] - pts[i][0], pts[i + 1][1] - pts[i][1])
    if (walked + seg >= half) {
      const t = seg ? (half - walked) / seg : 0
      return [pts[i][0] + (pts[i + 1][0] - pts[i][0]) * t, pts[i][1] + (pts[i + 1][1] - pts[i][1]) * t]
    }
    walked += seg
  }
  return [...pts[pts.length - 1]]
}

/** Ray-casting point-in-polygon test. */
export function pointInPolygon(pt, poly) {
  let inside = false
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i][0], yi = poly[i][1], xj = poly[j][0], yj = poly[j][1]
    if (((yi > pt[1]) !== (yj > pt[1])) && (pt[0] < ((xj - xi) * (pt[1] - yi)) / (yj - yi) + xi)) inside = !inside
  }
  return inside
}

/** Distance from a point to a line segment. */
export function distToSeg(p, a, b) {
  const dx = b[0] - a[0], dy = b[1] - a[1]
  const l2 = dx * dx + dy * dy
  let t = l2 ? ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2 : 0
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy))
}

/* ── Fractions → physical values ──────────────────────────────────────────── */

/** Visual fraction [fx, fy] → PDF points on the rotated visual page [X, Y]. */
export function fracToVisualPts(frac, frame) {
  const { vw, vh } = visualSize(frame)
  return [frac[0] * vw, frac[1] * vh]
}

/** Distance in PDF points (visual page) between two fraction points. */
export function fracDistPts(f1, f2, frame) {
  const a = fracToVisualPts(f1, frame), b = fracToVisualPts(f2, frame)
  return Math.hypot(b[0] - a[0], b[1] - a[1])
}

/**
 * scale_points_per_foot from a calibration pair: PDF points between the two
 * visual-fraction points divided by the entered real feet.
 */
export function scalePointsPerFoot(p1, p2, frame, feet) {
  if (!(feet > 0)) return null
  const pts = fracDistPts(p1, p2, frame)
  return pts > 0 ? pts / feet : null
}

/**
 * Region computed value from its stored fraction points:
 *   area → SF = shoelace(points²) / sppf²   line → LF = Σ segment pts / sppf
 *   count → 1 (one region per marker)
 * Returns null when the drawing is unscaled (except count).
 */
export function computeRegionValue(kind, fracPts, frame, sppf) {
  if (kind === 'count') return 1
  if (!(sppf > 0) || !Array.isArray(fracPts)) return null
  const P = fracPts.map(f => fracToVisualPts(f, frame))
  if (kind === 'area') return P.length >= 3 ? shoelaceArea(P) / (sppf * sppf) : null
  if (kind === 'line') return P.length >= 2 ? openPathLength(P) / sppf : null
  return null
}

/* ── Formatting ───────────────────────────────────────────────────────────── */

export function fmtSF(v) { return Math.round(v).toLocaleString() + ' SF' }
export function fmtLF(v) { return (Math.round(v * 10) / 10).toLocaleString() + ' LF' }

/** Display string for a region / category total by kind. */
export function fmtValue(kind, v) {
  if (v == null) return '—'
  if (kind === 'area') return fmtSF(v)
  if (kind === 'line') return fmtLF(v)
  return Math.round(v).toLocaleString()
}

/** Feet as ft-in display, e.g. 12.5 → 12'-6". */
export function feetLabel(ft) {
  const totalIn = Math.round(ft * 12)
  const feet = Math.floor(totalIn / 12), inches = totalIn % 12
  return feet > 0 ? (inches > 0 ? `${feet}'-${inches}"` : `${feet}'-0"`) : `${inches}"`
}
